import { useEffect, useState } from "react";
import { Icon } from "../../icons/Icon";
import { Aviso, Campo, Carregando } from "../../components/Layout";
import { api, fmt, type CandidatoPerfil, type ErroApi } from "../../lib/api";

/** Anexo II: item A = Rede Baixo Guandu (0,2 ponto/mês); item B = Outras Redes (0,1). */
type Rede = "MUNICIPAL_BG" | "ESTADUAL";
const REDES: Array<{ valor: Rede; rotulo: string }> = [
  { valor: "MUNICIPAL_BG", rotulo: "Rede Baixo Guandu" },
  { valor: "ESTADUAL", rotulo: "Outras Redes" },
];

type Experiencia = { instituicao: string; vinculo: Rede; dataInicio: string; dataFim: string };
type Qualificacao = { categoria: "I" | "II"; cargaHoraria: string; dataConclusao: string };

/**
 * Edição do perfil (dados pessoais, experiência, formação): reabre o que foi
 * salvo no cadastro, pra corrigir ou completar. Não altera pontuação de
 * inscrições já enviadas — elas são uma foto do perfil no momento do envio.
 */
export function Perfil() {
  const [carregando, setCarregando] = useState(true);
  const [erroCarregamento, setErroCarregamento] = useState<string | null>(null);

  const [nome, setNome] = useState("");
  const [cpf, setCpf] = useState("");
  const [dataNascimento, setDataNascimento] = useState("");
  const [rg, setRg] = useState("");
  const [rgOrgaoExpedidor, setRgOrgaoExpedidor] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [telefoneAlternativo, setTelefoneAlternativo] = useState("");
  const [logradouro, setLogradouro] = useState("");
  const [numero, setNumero] = useState("");
  const [complemento, setComplemento] = useState("");
  const [bairro, setBairro] = useState("");
  const [cidade, setCidade] = useState("");
  const [uf, setUf] = useState("");
  const [cep, setCep] = useState("");
  const [experiencias, setExperiencias] = useState<Experiencia[]>([]);
  const [qualificacoes, setQualificacoes] = useState<Qualificacao[]>([]);

  const [erros, setErros] = useState<string[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const [edicaoBloqueada, setEdicaoBloqueada] = useState(false);

  useEffect(() => {
    api.get<CandidatoPerfil>("/api/candidato/perfil")
      .then((p) => {
        setEdicaoBloqueada(p.edicaoBloqueada);
        setNome(p.nome); setCpf(fmt.cpf(p.cpf)); setDataNascimento(p.dataNascimento);
        setRg(p.rg); setRgOrgaoExpedidor(p.rgOrgaoExpedidor ?? "");
        setEmail(p.email); setTelefone(fmt.telefone(p.telefone));
        setTelefoneAlternativo(p.telefoneAlternativo ? fmt.telefone(p.telefoneAlternativo) : "");
        setLogradouro(p.logradouro); setNumero(p.numero); setComplemento(p.complemento ?? "");
        setBairro(p.bairro); setCidade(p.cidade); setUf(p.uf); setCep(fmt.cep(p.cep));
        setExperiencias(p.experiencias.map((e) => ({
          instituicao: e.instituicao, vinculo: e.vinculo as Rede,
          dataInicio: e.dataInicio, dataFim: e.dataFim ?? "",
        })));
        setQualificacoes(p.qualificacoes.map((q) => ({
          categoria: q.categoria,
          cargaHoraria: q.cargaHoraria ? String(q.cargaHoraria) : "", dataConclusao: q.dataConclusao ?? "",
        })));
      })
      .catch(() => setErroCarregamento("Não foi possível carregar o seu perfil."))
      .finally(() => setCarregando(false));
  }, []);

  function alterarExp(i: number, m: Partial<Experiencia>) {
    setExperiencias((lista) => lista.map((e, j) => (j === i ? { ...e, ...m } : e)));
  }
  function alterarQual(i: number, m: Partial<Qualificacao>) {
    setQualificacoes((lista) => lista.map((q, j) => (j === i ? { ...q, ...m } : q)));
  }

  async function salvar() {
    setSalvo(false);
    const problemas: string[] = [];
    qualificacoes.forEach((q, i) => {
      const rotulo = q.categoria === "I" ? "Formação acadêmica" : "Curso de formação";
      const n = i + 1;
      if (!q.cargaHoraria) problemas.push(`${rotulo} ${n}: informe a carga horária.`);
      if (q.categoria === "II" && !q.dataConclusao) {
        problemas.push(`${rotulo} ${n}: informe a data de conclusão.`);
      }
    });
    if (problemas.length) {
      setErros(problemas);
      window.scrollTo({ top: 0 });
      return;
    }
    setErros([]);
    setSalvando(true);
    try {
      await api.post("/api/publico/candidatos", {
        nome, cpf: cpf.replace(/\D/g, ""), dataNascimento, rg, rgOrgaoExpedidor,
        email, telefone: telefone.replace(/\D/g, ""),
        telefoneAlternativo: telefoneAlternativo.replace(/\D/g, "") || null,
        logradouro, numero, complemento, bairro, cidade, uf, cep: cep.replace(/\D/g, ""),
        experiencias: experiencias.map((e) => ({ ...e, dataFim: e.dataFim || null })),
        qualificacoes: qualificacoes.map((q) => ({
          ...q, cargaHoraria: q.cargaHoraria ? Number(q.cargaHoraria) : null,
          dataConclusao: q.dataConclusao || null,
        })),
      });
      setSalvo(true);
      window.scrollTo({ top: 0 });
    } catch (e) {
      const err = e as ErroApi;
      setErros(err.motivos.length ? err.motivos : [err.mensagem]);
      window.scrollTo({ top: 0 });
    } finally {
      setSalvando(false);
    }
  }

  if (carregando) return <Carregando texto="Carregando o seu perfil" />;
  if (erroCarregamento) return <Aviso tipo="erro">{erroCarregamento}</Aviso>;

  return (
    <div className="pilha pilha-6">
      <h1 className="h2">Meu perfil</h1>

      {edicaoBloqueada && (
        <Aviso tipo="atencao" titulo="Edição bloqueada">
          A Comissão bloqueou a edição do seu perfil. Os dados abaixo ficam disponíveis só para
          consulta. Se precisar corrigir algo, entre em contato com a SEMED.
        </Aviso>
      )}

      {salvo && <Aviso tipo="sucesso">Perfil atualizado.</Aviso>}
      {erros.length > 0 && (
        <Aviso tipo="erro"><ul>{erros.map((e, i) => <li key={i}>{e}</li>)}</ul></Aviso>
      )}

      <div className="tc-cartao pilha">
        <strong>Dados pessoais</strong>
        <Campo rotulo="Nome completo" id="p-nome" valor={nome} aoMudar={setNome} />
        <div className="grade grade-2">
          <Campo rotulo="CPF" id="p-cpf" valor={cpf} aoMudar={(v) => setCpf(fmt.cpf(v))} inputMode="numeric" />
          <Campo rotulo="Data de nascimento" id="p-nasc" tipo="date" valor={dataNascimento} aoMudar={setDataNascimento} />
        </div>
        <div className="grade grade-2">
          <Campo rotulo="RG" id="p-rg" valor={rg} aoMudar={setRg} />
          <Campo rotulo="Órgão expedidor" id="p-rgo" valor={rgOrgaoExpedidor} aoMudar={setRgOrgaoExpedidor} />
        </div>
        <div className="grade grade-2">
          <Campo rotulo="E-mail" id="p-email" tipo="email" valor={email} aoMudar={setEmail} />
          <Campo rotulo="Telefone" id="p-tel" valor={telefone} inputMode="tel" aoMudar={(v) => setTelefone(fmt.telefone(v))} />
        </div>
        <div className="grade grade-2">
          <Campo rotulo="CEP" id="p-cep" valor={cep} inputMode="numeric" aoMudar={(v) => setCep(fmt.cep(v))} />
          <Campo rotulo="Número" id="p-num" valor={numero} aoMudar={setNumero} />
        </div>
        <Campo rotulo="Logradouro" id="p-log" valor={logradouro} aoMudar={setLogradouro} />
        <div className="grade grade-2">
          <Campo rotulo="Bairro" id="p-bairro" valor={bairro} aoMudar={setBairro} />
          <Campo rotulo="Complemento" id="p-comp" valor={complemento} aoMudar={setComplemento} />
        </div>
        <div className="grade grade-2">
          <Campo rotulo="Cidade" id="p-cidade" valor={cidade} aoMudar={setCidade} />
          <Campo rotulo="Estado" id="p-uf" valor={uf} aoMudar={(v) => setUf(v.toUpperCase().slice(0, 2))} />
        </div>
      </div>

      <div className="tc-cartao pilha">
        <div className="linha linha-entre">
          <strong>Experiência profissional</strong>
          <button type="button" className="tc-btn tc-btn-outline"
                  onClick={() => setExperiencias((l) => [...l, { instituicao: "", vinculo: "MUNICIPAL_BG", dataInicio: "", dataFim: "" }])}>
            <Icon nome="editar" tamanho={16} />
            Adicionar
          </button>
        </div>
        {experiencias.map((e, i) => (
          <div key={i} className="pilha pilha-2" style={{ borderTop: "1px solid var(--tc-hairline)", paddingTop: 12 }}>
            <div className="linha linha-entre">
              <span className="tc-ajuda">Período {i + 1}</span>
              <button type="button" className="tc-btn tc-btn-outline"
                      onClick={() => setExperiencias((l) => l.filter((_, j) => j !== i))}>
                Remover
              </button>
            </div>
            <div className="tc-campo">
              <label>Rede de ensino</label>
              <select value={e.vinculo} onChange={(ev) => alterarExp(i, { vinculo: ev.target.value as Rede })}>
                {REDES.map((r) => <option key={r.valor} value={r.valor}>{r.rotulo}</option>)}
              </select>
            </div>
            <div className="grade grade-2">
              <Campo rotulo="Início" id={`p-exp-ini-${i}`} tipo="date" valor={e.dataInicio} aoMudar={(v) => alterarExp(i, { dataInicio: v })} />
              <Campo rotulo="Fim" id={`p-exp-fim-${i}`} tipo="date" valor={e.dataFim}
                     ajuda="Deixe em branco se ainda trabalha lá"
                     aoMudar={(v) => alterarExp(i, { dataFim: v })} />
            </div>
          </div>
        ))}
      </div>

      <div className="tc-cartao pilha">
        <div className="linha linha-entre">
          <strong>Formação</strong>
          <div className="linha linha-quebra">
            <button type="button" className="tc-btn tc-btn-outline"
                    onClick={() => setQualificacoes((l) => [...l, { categoria: "I", cargaHoraria: "", dataConclusao: "" }])}>
              + Formação acadêmica
            </button>
            <button type="button" className="tc-btn tc-btn-outline"
                    onClick={() => setQualificacoes((l) => [...l, { categoria: "II", cargaHoraria: "", dataConclusao: "" }])}>
              + Curso de formação
            </button>
          </div>
        </div>
        {qualificacoes.map((q, i) => (
          <div key={i} className="pilha pilha-2" style={{ borderTop: "1px solid var(--tc-hairline)", paddingTop: 12 }}>
            <div className="linha linha-entre">
              <span className="tc-ajuda">{q.categoria === "I" ? "Formação acadêmica" : "Curso de formação"}</span>
              <button type="button" className="tc-btn tc-btn-outline"
                      onClick={() => setQualificacoes((l) => l.filter((_, j) => j !== i))}>
                Remover
              </button>
            </div>
            <div className="grade grade-2">
              <Campo rotulo="Carga horária" id={`p-qual-ch-${i}`} inputMode="numeric" ajuda="Em horas"
                     valor={q.cargaHoraria} aoMudar={(v) => alterarQual(i, { cargaHoraria: v.replace(/\D/g, "") })} />
              <Campo rotulo="Data de conclusão" id={`p-qual-data-${i}`} tipo="date"
                     ajuda={q.categoria === "I" ? "Opcional" : undefined}
                     valor={q.dataConclusao} aoMudar={(v) => alterarQual(i, { dataConclusao: v })} />
            </div>
          </div>
        ))}
      </div>

      {!edicaoBloqueada && (
        <button type="button" className="tc-btn tc-btn-primario" onClick={salvar} disabled={salvando}>
          {salvando ? "Salvando..." : "Salvar perfil"}
        </button>
      )}
    </div>
  );
}
